<script setup lang="ts">
import type { TrackingT } from '~/types/tracking'
const props = defineProps<{
  data?: Partial<TrackingT>
  entry: string
}>()

const histories = computed(() => {
  /// Create a group from props.data.histories.created_at
  return props.data?.histories
    ?.slice()
    .sort(
      (a, b) =>
        new Date(b.created_at ?? new Date()).getTime() -
        new Date(a.created_at ?? new Date()).getTime()
    )
    .reduce(
      (acc, history) => {
        const date = new Date(
          history.created_at ?? new Date()
        ).toLocaleDateString('id-ID', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
        if (!acc[date]) {
          acc[date] = []
        }
        acc[date].push(history)
        return acc
      },
      {} as Record<string, typeof props.data.histories>
    )
})

const lastHistory = computed(() => {
  return props.data?.histories?.[0]
})
</script>
<template>
  <UiCardCollapsible
    default-open
    class="bg-surface-container-low border border-outline-variant shadow-none"
  >
    <template #header>
      <UiButtonClipboard
        :text="props.entry"
        class="font-semibold"
        show-text
        size="16px"
      />
    </template>
    <template v-if="props.data && props.data.status === true">
      <div class="tracking-content" style="display: block">
        <div
          class="grid grid-cols-1 md:grid-cols-2 gap-2 border-b pb-3 border-dashed border-outline-variant"
        >
          <div class="text-sm">
            <p class="font-semibold text-primary">
              {{ props.data?.details?.origin?.name }}
            </p>
            <p>
              {{ props.data?.details?.origin?.city }}
            </p>
            <p class="font-semibold">
              {{ props.data?.details?.origin?.province }}
            </p>
          </div>
          <div class="text-sm">
            <p class="font-semibold text-primary">
              {{ props.data?.details?.destination?.name }}
            </p>
            <p>
              {{ props.data?.details?.destination?.city }}
            </p>
            <p class="font-semibold">
              {{ props.data?.details?.destination?.province }}
            </p>
          </div>
        </div>
        <div
          v-if="props.data && props.data.status === true"
          class="grid grid-cols-2 gap-1 text-sm py-3 border-b border-dashed border-outline-variant"
        >
          <div>Kurir</div>
          <div class="font-semibold text-right">
            {{ props.data?.details?.service }}
          </div>
          <div>Status</div>
          <div class="font-semibold text-right">
            <UiBadge
              class="inline-flex"
              :label="mapKajStatus(props.data?.status_code ?? 100).label"
              :variant="mapKajStatus(props.data?.status_code ?? 100).color"
            />
          </div>
        </div>
        <div class="py-3">
          <div
            v-for="(chunks, index) in histories"
            :key="index"
            class="space-y-2"
          >
            <div class="flex gap-3">
              <div class="space-y-2 text-sm relative">
                <div
                  class="font-semibold pl-6 relative"
                  :class="{
                    'text-primary': chunks.some(
                      (history) =>
                        history.created_at === lastHistory?.created_at
                    )
                  }"
                >
                  {{ index }}
                  <span
                    class="block w-2 h-2 absolute rounded-full left-1 top-1/2 bg-current -translate-y-1/2"
                  ></span>
                </div>

                <span
                  class="block w-px bottom-0 absolute left-1 top-4 border-l border-outline-variant border-dashed translate-x-1"
                ></span>

                <div
                  v-for="(history, historyIndex) in chunks"
                  :key="historyIndex"
                  class="flex gap-3 text-normal items-start pl-6"
                  :class="{
                    'text-primary':
                      history.created_at === lastHistory?.created_at &&
                      historyIndex === 0
                  }"
                >
                  <span
                    v-text="
                      new Date(
                        history.created_at ?? new Date()
                      ).toLocaleTimeString('id-ID', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      })
                    "
                  />
                  <p>{{ history.status }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <UiAlert
        class="mt-3"
        variant="error"
        title="Data tidak ditemukan"
        description="Pastikan nomor resi dan ekspedisi yang Anda masukkan sudah benar."
      />
    </template>
  </UiCardCollapsible>
</template>
